import {sleepMs} from '@coolcolduk/util';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import {GoogleAuthProvider, signInWithEmailAndPassword, signInWithPopup} from 'firebase/auth';
import {useState} from 'react';
import {useNavigate} from 'react-router';
import {env} from '../../config/env';
import {firebaseAuth} from '../../config/firebase';
import EmailSignInForm from './EmailSignInForm';
import GoogleSignInButton from './GoogleSignInButton';
import LoginHeader from './LoginHeader';
import TermsLinks from './TermsLinks';

export default function LoginForm() {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [usernamePasswordLoading, setUsernamePasswordLoading] = useState(false);
  const navigate = useNavigate();

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account',
      });

      await signInWithPopup(firebaseAuth, provider);
      navigate('/');
    } catch (err: any) {
      // Error logged for debugging
      setError(err.message || 'Failed to sign in with Google');
    } finally {
      setLoading(false);
    }
  };

  const handleUsernamePasswordSignIn = async (email: string, password: string) => {
    setError('');
    setUsernamePasswordLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(firebaseAuth, email, password);

      // Wait a moment for the auth state to update
      await sleepMs(100);

      // Verify the token is available
      await userCredential.user.getIdToken();

      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Failed to sign in with email and password');
    } finally {
      setUsernamePasswordLoading(false);
    }
  };

  return (
    <Paper
      elevation={3}
      data-testid="login-form"
      sx={{
        p: 4,
        maxWidth: 400,
        width: '100%',
      }}
    >
      <LoginHeader />

      <Box sx={{mt: 4}}>
        {error && (
          <Alert severity="error" sx={{mb: 3}}>
            {error}
          </Alert>
        )}

        <GoogleSignInButton
          loading={loading}
          disabled={loading || usernamePasswordLoading}
          onSignIn={handleGoogleSignIn}
        />

        {env.allowUsernamePassword && (
          <EmailSignInForm
            loading={usernamePasswordLoading}
            disabled={loading || usernamePasswordLoading}
            onSignIn={handleUsernamePasswordSignIn}
          />
        )}

        <TermsLinks />
      </Box>
    </Paper>
  );
}
